import { yupResolver } from '@hookform/resolvers/yup';
import { BusinessFormatMaster, MenuCategoryMaster } from 'models/masterdata';
import { RefObject, memo, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { MenuCategoryMasterListFormValues } from '..';

const COLUMN_WIDTH = [50, 220, 220, 130];

export interface MenuCategoryMasterListProps {
  businessTypeData: BusinessFormatMaster[];
  formRef: RefObject<HTMLFormElement>;
  data: MenuCategoryMaster[];
  onSubmitForm?: (formValues: MenuCategoryMasterListFormValues) => void;
}

function MenuCategoryMasterList({ businessTypeData, formRef, data, onSubmitForm }: MenuCategoryMasterListProps) {
  const { t } = useTranslation();

  // schema validation
  const schema = yup.object({
    data: yup
      .array(
        yup.object({
          menu_category_cd: yup.number().required(),
          business_format_master: yup.string().required(t('MenuCategoryMaster.warn_field_required')),
          menu_category_name: yup.string().required(t('MenuCategoryMaster.warn_field_required')),
          display_order: yup
            .number()
            .transform((value, originalValue) => {
              if (typeof originalValue === 'string' && originalValue.trim() === '') {
                return undefined;
              }
              return value;
            })
            .min(1, t('MenuCategoryMaster.warn_number_required'))
            .integer(t('MenuCategoryMaster.warn_number_required'))
            .required(t('MenuCategoryMaster.warn_number_required')),
          deleted: yup.boolean().required(),
        })
      )
      .required(),
  });

  // form setup
  const {
    register,
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<MenuCategoryMasterListFormValues>({
    resolver: yupResolver(schema),
    defaultValues: { data: data },
    mode: 'onSubmit',
  });
  const { fields } = useFieldArray({
    name: 'data',
    control,
    // keyName: 'key',
  });

  // side effects
  useEffect(() => {
    reset({
      data: data,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  //events
  function onSubmit(formValues: MenuCategoryMasterListFormValues) {
    onSubmitForm?.(formValues);
  }

  return (
    <div className="sticky-table flex-grow-1 flex-shrink-1">
      <Form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
        <div className="div-table fixsize-column">
          <div className="div-thead div-tr">
            <div className="div-th" style={{ width: `${COLUMN_WIDTH[0]}px` }}>
              {t('MenuCategoryMaster.delete')}
            </div>
            <div className="div-th" style={{ width: `${COLUMN_WIDTH[1]}px` }}>
              {t('MenuCategoryMaster.business_type')}
            </div>
            <div className="div-th" style={{ width: `${COLUMN_WIDTH[2]}px` }}>
              {t('MenuCategoryMaster.name')}
            </div>
            <div className="div-th" style={{ width: `${COLUMN_WIDTH[3]}px` }}>
              {t('MenuCategoryMaster.display_order')}
            </div>
          </div>

          {fields.map((field, index) => {
            return (
              <div className="div-tr" key={field.menu_category_cd}>
                <div
                  className="div-td d-flex align-items-center justify-content-center"
                  style={{ width: `${COLUMN_WIDTH[0]}px` }}
                >
                  <Form.Check
                    {...register(`data.${index}.deleted`)}
                    className="anra-checkbox d-flex justify-content-center"
                    label=""
                    type="checkbox"
                  />
                </div>
                <div className="div-td" style={{ width: `${COLUMN_WIDTH[1]}px` }}>
                  <Form.Select
                    {...register(`data.${index}.business_format_master`)}
                    isInvalid={!!errors?.data?.[index]?.business_format_master}
                  >
                    {businessTypeData.length > 0 &&
                      businessTypeData.map((option) => (
                        <option key={option.business_format_cd} value={option.business_format_cd}>
                          {option.name}
                        </option>
                      ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors?.data?.[index]?.business_format_master?.message}
                  </Form.Control.Feedback>
                </div>
                <div className="div-td" style={{ width: `${COLUMN_WIDTH[2]}px` }}>
                  <Form.Control
                    {...register(`data.${index}.menu_category_name`)}
                    type="text"
                    placeholder=""
                    isInvalid={!!errors?.data?.[index]?.menu_category_name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors?.data?.[index]?.menu_category_name?.message}
                  </Form.Control.Feedback>
                </div>
                <div className="div-td" style={{ width: `${COLUMN_WIDTH[3]}px` }}>
                  <Form.Control
                    {...register(`data.${index}.display_order`)}
                    className="text-end"
                    type="number"
                    placeholder=""
                    min={1}
                    isInvalid={!!errors?.data?.[index]?.display_order}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors?.data?.[index]?.display_order?.message}
                  </Form.Control.Feedback>
                </div>
              </div>
            );
          })}
        </div>
      </Form>
    </div>
  );
}

export default memo(MenuCategoryMasterList);
